import { useEffect, useRef, useState, useCallback } from 'react';
import moment from 'moment';

import processHtmlToReact from '@/utils/helpers/processHtml';
import { Avatar } from '@/components/Shared/Avatar';

import css from './RetroMessage.module.css';
import { Profile } from '@/api/types/profile-types';
import { Message as MessageResponse } from '@/api/types/message-types';
import { ms } from 'date-fns/locale';

interface MessageGroupProps {
  messages: MessageResponse[];
  profile: Profile;
  showAvatar?: boolean;
  isOwn: boolean;
  scrollIntoViewId?: string | null;
  onScrollIntoView?: () => void;
  onReply?: (messageId: string, profileId: string, nickname: string) => void;
  onEdit?: (messageId: string, content: string) => void;
  onDelete?: (messageId: string) => void;
}

export function Message({
  messages,
  profile,
  isOwn,
  showAvatar = true,
  scrollIntoViewId,
  onScrollIntoView,
  onReply,
  onEdit,
  onDelete
}: MessageGroupProps) {
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; messageId: string } | null>(null);
  const [touchTimer, setTouchTimer] = useState<number | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Scroll to the specified message
  useEffect(() => {
    if (scrollIntoViewId) {
      const el = messageRefs.current.get(scrollIntoViewId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onScrollIntoView?.();
      }
    }
  }, [scrollIntoViewId, onScrollIntoView]);

  // Close context menu when clicking outside
  useEffect(() => {
    if (!contextMenu) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  const handleContextMenu = useCallback((e: React.MouseEvent, messageId: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, messageId });
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent, messageId: string) => {
    const timer = window.setTimeout(() => {
      const touch = e.touches[0];
      setContextMenu({ x: touch.clientX, y: touch.clientY, messageId });
    }, 500);
    setTouchTimer(timer);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchTimer) {
      clearTimeout(touchTimer);
      setTouchTimer(null);
    }
  }, [touchTimer]);

  const handleMenuAction = useCallback(
    (action: 'reply' | 'edit' | 'delete') => {
      if (!contextMenu) return;
      const { messageId } = contextMenu;
      const message = messages.find(m => m.id === messageId);
      setContextMenu(null);
      switch (action) {
        case 'reply':
          onReply?.(messageId, profile.id, profile.nickname);
          break;
        case 'edit':
          if (message) onEdit?.(messageId, message.content);
          break;
        case 'delete':
          onDelete?.(messageId);
          break;
      }
    },
    [contextMenu, messages, profile.id, profile.nickname, onReply, onEdit, onDelete]
  );

  return (
    <>
      <div className={`${css.message} ${isOwn ? css.ownMessage : ''}`} data-testid="message">
        <div className={`${css.avatar} ${!showAvatar ? css.avatarHidden : ''}`}>
          <Avatar profile={profile} />
        </div>
        <div className={`${css.messageContainer} ${isOwn ? css.ownMessageContainer : ''}`}>
          {showAvatar && (
            <div className={css.messageMeta}>
              <div className={css.messageAuthor}>{profile.nickname}</div>
            </div>
          )}

          <div className={css.messageGroup}>
            {messages.map((msg, index) => {
              const isLastInGroup = index === messages.length - 1;
              const processedContent = processHtmlToReact(msg.content);
              return (
                <div
                  key={msg.id}
                  id={msg.id}
                  ref={el => {
                    if (el) messageRefs.current.set(msg.id, el);
                    else messageRefs.current.delete(msg.id);
                  }}
                  className={css.messageItem}
                  onContextMenu={e => handleContextMenu(e, msg.id)}
                  onTouchStart={e => handleTouchStart(e, msg.id)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={handleTouchEnd}
                >
                  <div className={`${css.messageBody} ${!isLastInGroup ? css.messageBodyFollowUp : ''}`}>
                    {processedContent}
                  </div>
                  <div className={css.messageDate}>{moment(msg.createdAt).format('HH:mm')}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {contextMenu && (
        <div ref={contextMenuRef} className={css.contextMenu} style={{ top: contextMenu.y, left: contextMenu.x }}>
          <button className={css.contextMenuItem} onClick={() => handleMenuAction('reply')}>
            Ответить
          </button>
          {isOwn && (
            <>
              <button className={css.contextMenuItem} onClick={() => handleMenuAction('edit')}>
                Редактировать
              </button>
              <button className={css.contextMenuItem} onClick={() => handleMenuAction('delete')}>
                Удалить
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
